'use client';

/**
 * Shared form-field primitives (TextField / SelectField / TextAreaField).
 *
 * These own label wiring, ids, error display, and theme-token styling so
 * pages never repeat the raw input className string. Analogous to the
 * mandatory Button/ButtonLink rule: always use these instead of raw
 * <input> / <select> / <textarea> with custom Tailwind (see AGENTS.md,
 * Cross-Cutting Requirements, Form Field Component Usage).
 */

import { useId, type ChangeEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** Theme-token control styling shared by every field primitive. */
export const FORM_CONTROL_CLASSES =
  'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent';

const LABEL_CLASSES = 'block text-sm font-medium text-primary mb-1.5';

const controlClassName = (hasError: boolean, extra?: string) =>
  cn(
    FORM_CONTROL_CLASSES,
    hasError ? 'border-danger' : 'border-secondary',
    extra
  );

export interface FieldBaseProps {
  /** Explicit control id; auto-generated when omitted. */
  id?: string;
  label?: string;
  /** Render the label for screen readers only. */
  hideLabel?: boolean;
  /** Validation error shown below the control and wired via aria. */
  error?: string;
  /** Sets the HTML required attribute and shows the required mark. */
  required?: boolean;
  /** Shows the required mark without enforcing HTML validation. */
  markRequired?: boolean;
  disabled?: boolean;
  /** Class for the wrapping element. */
  className?: string;
  /** Extra classes merged onto the control element. */
  controlClassName?: string;
}

interface FieldChromeProps {
  id: string;
  label?: string;
  hideLabel?: boolean;
  showRequiredMark: boolean;
  error?: string;
  errorId: string;
  className?: string;
  children: ReactNode;
}

function FieldChrome({
  id,
  label,
  hideLabel,
  showRequiredMark,
  error,
  errorId,
  className,
  children,
}: FieldChromeProps) {
  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className={hideLabel ? 'sr-only' : LABEL_CLASSES}>
          {label}
          {showRequiredMark && <span aria-hidden="true"> *</span>}
        </label>
      )}
      {children}
      {error && (
        <p id={errorId} role="alert" className="text-sm text-danger mt-1">
          {error}
        </p>
      )}
    </div>
  );
}

function useFieldIds(explicitId?: string) {
  const generatedId = useId();
  const id = explicitId ?? generatedId;
  return { id, errorId: `${id}-error` };
}

const ariaProps = (error: string | undefined, errorId: string) => ({
  'aria-invalid': error ? true : undefined,
  'aria-describedby': error ? errorId : undefined,
});

export interface TextFieldProps extends FieldBaseProps {
  value: string | number;
  onChange: (value: string) => void;
  type?:
    | 'text'
    | 'number'
    | 'date'
    | 'email'
    | 'tel'
    | 'password'
    | 'url'
    | 'search';
  placeholder?: string;
  name?: string;
  autoComplete?: string;
  min?: number | string;
  max?: number | string;
  step?: number | string;
  'aria-label'?: string;
}

export function TextField({
  id: explicitId,
  label,
  hideLabel,
  error,
  required,
  markRequired,
  disabled,
  className,
  controlClassName: extraControlClassName,
  value,
  onChange,
  type = 'text',
  placeholder,
  name,
  autoComplete,
  min,
  max,
  step,
  'aria-label': ariaLabel,
}: TextFieldProps) {
  const { id, errorId } = useFieldIds(explicitId);

  return (
    <FieldChrome
      id={id}
      label={label}
      hideLabel={hideLabel}
      showRequiredMark={Boolean(required || markRequired)}
      error={error}
      errorId={errorId}
      className={className}
    >
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        placeholder={placeholder}
        name={name}
        autoComplete={autoComplete}
        min={min}
        max={max}
        step={step}
        required={required}
        disabled={disabled}
        aria-label={ariaLabel}
        {...ariaProps(error, errorId)}
        className={controlClassName(Boolean(error), extraControlClassName)}
      />
    </FieldChrome>
  );
}

export interface SelectFieldOption {
  value: string;
  label: string;
}

export interface SelectFieldProps extends FieldBaseProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectFieldOption[];
  name?: string;
  'aria-label'?: string;
}

export function SelectField({
  id: explicitId,
  label,
  hideLabel,
  error,
  required,
  markRequired,
  disabled,
  className,
  controlClassName: extraControlClassName,
  value,
  onChange,
  options,
  name,
  'aria-label': ariaLabel,
}: SelectFieldProps) {
  const { id, errorId } = useFieldIds(explicitId);

  return (
    <FieldChrome
      id={id}
      label={label}
      hideLabel={hideLabel}
      showRequiredMark={Boolean(required || markRequired)}
      error={error}
      errorId={errorId}
      className={className}
    >
      <select
        id={id}
        value={value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          onChange(e.target.value)
        }
        name={name}
        required={required}
        disabled={disabled}
        aria-label={ariaLabel}
        {...ariaProps(error, errorId)}
        className={controlClassName(Boolean(error), extraControlClassName)}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldChrome>
  );
}

export interface TextAreaFieldProps extends FieldBaseProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  name?: string;
  'aria-label'?: string;
}

export function TextAreaField({
  id: explicitId,
  label,
  hideLabel,
  error,
  required,
  markRequired,
  disabled,
  className,
  controlClassName: extraControlClassName,
  value,
  onChange,
  rows,
  placeholder,
  name,
  'aria-label': ariaLabel,
}: TextAreaFieldProps) {
  const { id, errorId } = useFieldIds(explicitId);

  return (
    <FieldChrome
      id={id}
      label={label}
      hideLabel={hideLabel}
      showRequiredMark={Boolean(required || markRequired)}
      error={error}
      errorId={errorId}
      className={className}
    >
      <textarea
        id={id}
        value={value}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        rows={rows}
        placeholder={placeholder}
        name={name}
        required={required}
        disabled={disabled}
        aria-label={ariaLabel}
        {...ariaProps(error, errorId)}
        className={controlClassName(Boolean(error), extraControlClassName)}
      />
    </FieldChrome>
  );
}
